import React from 'react'
// PRO-130: a deep `antd/es/*` subpath, NOT the `antd` root barrel. This module
// is on the boot-static graph (AntdI18n renders <Empty/>); importing from
// 'antd' pulls antd/es/index.js — the whole component barrel (Select/Table/
// Tree/DatePicker → rc-select/rc-table/rc-tree/rc-picker) — into the entry
// chunk. Only the two preset illustrations are taken from it here.
import AntdEmpty from 'antd/es/empty'
import type { EmptyProps } from 'antd/es/empty'

import { useDesignMode } from 'src/design-mode/useDesignMode'

import {
  Empty as AntdSeamEmpty,
  EmptySelectItem as AntdSeamEmptySelectItem,
} from './Empty.antd'
import {
  Empty as ShadcnSeamEmpty,
  EmptySelectItem as ShadcnSeamEmptySelectItem,
} from './Empty.shadcn'

type TEmptyProps = EmptyProps & {
  withoutWrapper?: boolean
}

/**
 * Compat seam for antd's Empty — one of the six presentational components put
 * behind the design toggle in the same PR (Input, Card, Alert, Divider, Empty,
 * Skeleton), after `../Button` (#970), `../Typography` + `../Space` (#971) and
 * `../Tag` (#972).
 *
 * ---------------------------------------------------------------------------
 * THE SWITCH, and its safety property
 *
 * `useDesignMode()` returns `'antd'` for everyone who has not picked "New
 * design" under Theme — which is the shipped default, so it is every page load
 * of every customer who has not opted in. In the `'antd'` branch this component renders
 * `./Empty.antd` with the props it was given: same element, same class names,
 * same DOM. The only difference from the previous `./Empty.tsx` — which IS
 * `./Empty.antd`, moved — is one extra component in the React tree, which
 * renders nothing of its own. `./__tests__/empty-seam-parity.test.tsx` asserts
 * that byte for byte across a prop matrix.
 *
 * LIKE `../Tag`, THE ANTD BRANCH IS NOT ANTD. `src/antd` has SHADOWED Empty
 * since long before the toggle existed — the full-height centred wrapper, the
 * WasteHero placeholder PNG and the "No data found" default all come from the
 * seam, not from antd. "Unchanged" therefore means unchanged versus
 * `./Empty.antd`, and the parity test compares against that. The 12 files
 * still importing `Empty` from raw `antd` are the documented deferred residual
 * (AGENTS.md); the toggle does not reach them, by design.
 *
 * THE TWO PRESET STATICS DO NOT DISPATCH, and that is deliberate. They are
 * VALUES read at module scope, not components rendered per call, so there is
 * no hook to dispatch on — and they must not change anyway: 52 call sites pass
 * `Empty.PRESENTED_IMAGE_SIMPLE` straight back in as `image`, and swapping the
 * element would change the antd branch's DOM. antd's own illustrations paint
 * from inline `fill`/`stroke` attributes computed by `useToken()`, not from
 * `.ant-empty-img-*` CSS, so they render the same in either mode.
 * `EmptySelectItem` DOES dispatch, exactly as `Empty` does, so the seam cannot
 * end up half-switched.
 * ------------------------------------------------------------------------- */
const EmptyDispatcher = (props: TEmptyProps) => {
  const designMode = useDesignMode()

  if (designMode === 'shadcn') return <ShadcnSeamEmpty {...props} />

  return <AntdSeamEmpty {...props} />
}

EmptyDispatcher.displayName = 'Empty'

export const EmptySelectItem = (
  props: React.ComponentProps<typeof AntdSeamEmptySelectItem>
) => {
  const designMode = useDesignMode()

  if (designMode === 'shadcn') return <ShadcnSeamEmptySelectItem {...props} />

  return <AntdSeamEmptySelectItem {...props} />
}

EmptySelectItem.displayName = 'EmptySelectItem'

export const Empty = Object.assign(EmptyDispatcher, {
  PRESENTED_IMAGE_SIMPLE: AntdEmpty.PRESENTED_IMAGE_SIMPLE,
  PRESENTED_IMAGE_DEFAULT: AntdEmpty.PRESENTED_IMAGE_DEFAULT,
})
